<template>
  <div class="px-4 py-6">
    <div class="relative flex flex-col bg-white w-full mb-6 shadow-lg rounded-lg">
      <!-- Header -->
      <div class="flex flex-wrap justify-between items-start gap-4 mb-4 mt-4 mx-4">
        <div>
          <h2 class="text-2xl font-semibold text-blueGray-800">
            My Expenditure
          </h2>
          <p class="text-sm text-blueGray-500 mt-1">
            Review authorized budget, monthly expenses, total expenses, and remaining balance by service code.
          </p>
          <p v-if="selectedPlan" class="text-xs text-blueGray-400 mt-1">
            Budget Period: {{ formatDate(selectedPlan.from) }} - {{ formatDate(selectedPlan.to) }}
          </p>
        </div>

        <div class="flex flex-wrap gap-2">
          <button
            v-for="option in filterOptions"
            :key="option"
            @click="activeFilter = option"
            class="px-4 py-2 rounded font-bold uppercase text-xs transition-all"
            :class="
              activeFilter === option
                ? 'bg-lightBlue-600 text-white shadow'
                : 'bg-white text-blueGray-600 border hover:bg-blueGray-50'
            "
          >
            {{ option }}
          </button>
        </div>
      </div>

      <!-- Summary Cards -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mx-4 mb-4">
        <div class="rounded-lg bg-blueGray-50 p-4 border">
          <p class="text-xs uppercase font-bold text-blueGray-500">Authorized Budget</p>
          <p class="text-xl font-bold text-blueGray-800 mt-2">
            {{ formatCurrency(totalAuthorized) }}
          </p>
        </div>

        <div class="rounded-lg bg-blueGray-50 p-4 border">
          <p class="text-xs uppercase font-bold text-blueGray-500">Expenses This Month</p>
          <p class="text-xl font-bold text-blueGray-800 mt-2">
            {{ formatCurrency(totalThisMonth) }}
          </p>
        </div>

        <div class="rounded-lg bg-blueGray-50 p-4 border">
          <p class="text-xs uppercase font-bold text-blueGray-500">Total Expenses</p>
          <p class="text-xl font-bold text-blueGray-800 mt-2">
            {{ formatCurrency(totalExpenses) }}
          </p>
        </div>

        <div class="rounded-lg bg-blueGray-50 p-4 border">
          <p class="text-xs uppercase font-bold text-blueGray-500">Amount Remaining</p>
          <p
            class="text-xl font-bold mt-2"
            :class="totalRemaining < 0 ? 'text-red-600' : 'text-emerald-600'"
          >
            {{ formatCurrency(totalRemaining) }}
          </p>
        </div>
      </div>

      <!-- Search -->
      <div class="mx-4 mb-4">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search by service code, description, provider, or domain..."
          class="w-full border rounded-lg px-4 py-3 text-sm text-blueGray-700 focus:outline-none focus:ring-2 focus:ring-lightBlue-500"
        />
      </div>

      <!-- Loading / Error -->
      <div v-if="isLoading" class="px-6 py-8 text-center text-blueGray-500">
        Loading expenditure records...
      </div>

      <div v-else-if="errorMessage" class="mx-4 mb-4 p-4 rounded bg-red-50 text-red-600 font-semibold">
        {{ errorMessage }}
      </div>

      <!-- Table -->
      <div v-else class="block w-full overflow-x-auto">
        <table class="w-full border-collapse expenditure-table">
          <thead class="bg-blueGray-700 text-white">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase">Service Code</th>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase">Description</th>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase">Domain</th>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase">Provider / Vendor / Employee</th>
              <th class="px-4 py-3 text-right text-xs font-semibold uppercase">Amount Authorized</th>
              <th class="px-4 py-3 text-right text-xs font-semibold uppercase">Expenses This Month</th>
              <th class="px-4 py-3 text-right text-xs font-semibold uppercase">Total Expenses</th>
              <th class="px-4 py-3 text-right text-xs font-semibold uppercase">Amount Remaining</th>
              <th class="px-4 py-3 text-center text-xs font-semibold uppercase">Usage</th>
              <th class="px-4 py-3 text-center text-xs font-semibold uppercase">Show Detail</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="row in filteredRows"
              :key="row.service_code"
              class="hover:bg-blueGray-50 transition-all border-b"
            >
              <td class="px-4 py-4 align-top font-bold text-blueGray-800 whitespace-nowrap">
                {{ row.service_code || "Unassigned" }}
              </td>

              <td class="px-4 py-4 align-top min-w-56">
                {{ row.service_description || "—" }}
              </td>

              <td class="px-4 py-4 align-top min-w-48">
                <span class="px-2 py-1 rounded bg-blueGray-100 text-blueGray-700 text-xs font-bold">
                  {{ row.domain || "Unassigned" }}
                </span>
              </td>

              <td class="px-4 py-4 align-top min-w-64 text-blueGray-700">
                <div v-if="row.provider_display">
                  {{ row.provider_display }}
                </div>
                <div v-else class="text-blueGray-400 italic">
                  No expense provider yet
                </div>
              </td>

              <td class="px-4 py-4 align-top text-right font-bold">
                {{ formatCurrency(row.authorized_amount) }}
              </td>

              <td class="px-4 py-4 align-top text-right">
                {{ formatCurrency(row.expenses_this_month) }}
              </td>

              <td class="px-4 py-4 align-top text-right font-bold text-lightBlue-700">
                {{ formatCurrency(row.total_expenses) }}
              </td>

              <td
                class="px-4 py-4 align-top text-right font-bold"
                :class="Number(row.amount_remaining || 0) < 0 ? 'text-red-600' : 'text-emerald-600'"
              >
                {{ formatCurrency(row.amount_remaining) }}
              </td>

              <td class="px-4 py-4 align-top text-center min-w-40">
                <div class="usage-bar">
                  <div
                    class="usage-bar-fill"
                    :class="usagePercent(row) > 100 ? 'usage-over' : 'usage-normal'"
                    :style="{ width: Math.min(usagePercent(row), 100) + '%' }"
                  ></div>
                </div>
                <p class="text-xs text-blueGray-500 mt-1">
                  {{ usagePercent(row).toFixed(1) }}%
                </p>
              </td>

              <td class="px-4 py-4 align-top text-center">
                <button
                  type="button"
                  class="px-3 py-2 rounded bg-lightBlue-600 text-white text-xs font-bold hover:bg-lightBlue-700"
                  @click="openDetail(row)"
                >
                  View
                </button>
              </td>
            </tr>

            <tr v-if="!filteredRows.length">
              <td colspan="10" class="text-center py-8 text-blueGray-400">
                No expenditure records found.
              </td>
            </tr>
          </tbody>

          <tfoot>
            <tr class="bg-blueGray-50 font-semibold">
              <td colspan="4" class="px-4 py-4 text-right text-blueGray-700">
                Total for {{ activeFilter }}:
              </td>
              <td class="px-4 py-4 text-right text-blueGray-800">
                {{ formatCurrency(filteredAuthorized) }}
              </td>
              <td class="px-4 py-4 text-right text-blueGray-800">
                {{ formatCurrency(filteredThisMonth) }}
              </td>
              <td class="px-4 py-4 text-right text-lightBlue-700">
                {{ formatCurrency(filteredExpenses) }}
              </td>
              <td
                class="px-4 py-4 text-right text-lg"
                :class="filteredRemaining < 0 ? 'text-red-600' : 'text-emerald-600'"
              >
                {{ formatCurrency(filteredRemaining) }}
              </td>
              <td colspan="2"></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- Detail Modal -->
    <div v-if="selectedRow" class="modal-backdrop" @click.self="closeDetail">
      <div class="modal-card">
        <div class="flex justify-between items-start mb-4">
          <div>
            <h3 class="text-xl font-bold text-blueGray-800">
              Service Code {{ selectedRow.service_code }}
            </h3>
            <p class="text-sm text-blueGray-500 mt-1">
              {{ selectedRow.service_description }}
            </p>
          </div>

          <button
            type="button"
            class="text-blueGray-400 hover:text-blueGray-700 text-2xl"
            @click="closeDetail"
          >
            ×
          </button>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
          <div class="rounded bg-blueGray-50 p-3 border">
            <p class="text-xs uppercase font-bold text-blueGray-500">Authorized</p>
            <p class="font-bold text-blueGray-800">{{ formatCurrency(selectedRow.authorized_amount) }}</p>
          </div>
          <div class="rounded bg-blueGray-50 p-3 border">
            <p class="text-xs uppercase font-bold text-blueGray-500">This Month</p>
            <p class="font-bold text-blueGray-800">{{ formatCurrency(selectedRow.expenses_this_month) }}</p>
          </div>
          <div class="rounded bg-blueGray-50 p-3 border">
            <p class="text-xs uppercase font-bold text-blueGray-500">Total Expenses</p>
            <p class="font-bold text-blueGray-800">{{ formatCurrency(selectedRow.total_expenses) }}</p>
          </div>
          <div class="rounded bg-blueGray-50 p-3 border">
            <p class="text-xs uppercase font-bold text-blueGray-500">Remaining</p>
            <p
              class="font-bold"
              :class="Number(selectedRow.amount_remaining || 0) < 0 ? 'text-red-600' : 'text-emerald-600'"
            >
              {{ formatCurrency(selectedRow.amount_remaining) }}
            </p>
          </div>
        </div>

        <div class="overflow-x-auto">
          <table class="w-full border-collapse expenditure-table">
            <thead class="bg-blueGray-100">
              <tr>
                <th class="px-3 py-2 text-left text-xs font-bold uppercase">Date</th>
                <th class="px-3 py-2 text-left text-xs font-bold uppercase">Type</th>
                <th class="px-3 py-2 text-left text-xs font-bold uppercase">Provider</th>
                <th class="px-3 py-2 text-left text-xs font-bold uppercase">Description</th>
                <th class="px-3 py-2 text-left text-xs font-bold uppercase">Status</th>
                <th class="px-3 py-2 text-right text-xs font-bold uppercase">Amount</th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="detail in selectedRow.details || []"
                :key="detail.id"
                class="border-b"
              >
                <td class="px-3 py-3 whitespace-nowrap">
                  {{ formatDate(detail.expense_date) }}
                </td>
                <td class="px-3 py-3">
                  {{ detail.source_type }}
                </td>
                <td class="px-3 py-3 font-semibold">
                  {{ detail.provider_name }}
                </td>
                <td class="px-3 py-3 min-w-72">
                  {{ detail.description }}
                </td>
                <td class="px-3 py-3">
                  {{ detail.status }}
                </td>
                <td class="px-3 py-3 text-right font-bold">
                  {{ formatCurrency(detail.total_expense) }}
                </td>
              </tr>

              <tr v-if="!(selectedRow.details || []).length">
                <td colspan="6" class="text-center py-6 text-blueGray-400">
                  No detailed expenses yet for this service code.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="mt-4 text-right">
          <button
            type="button"
            class="px-4 py-2 rounded bg-blueGray-700 text-white text-sm font-bold"
            @click="closeDetail"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "SpendingHistory",
  data() {
    return {
      activeFilter: "All",
      filterOptions: [
        "All",
        "Living Arrangements",
        "Employment & Community Participation",
        "Health & Safety",
      ],
      plans: [],
      selectedRow: null,
      searchQuery: "",
      isLoading: false,
      errorMessage: "",
    };
  },
  computed: {
    selectedPlan() {
      return this.plans.length ? this.plans[0] : null;
    },
    rows() {
      if (!this.selectedPlan || !this.selectedPlan.expenditure_summary) {
        return [];
      }

      return this.selectedPlan.expenditure_summary;
    },
    filteredRows() {
      let rows = this.rows;

      if (this.activeFilter !== "All") {
        rows = rows.filter((row) => row.domain === this.activeFilter);
      }

      const query = this.searchQuery.trim().toLowerCase();

      if (query) {
        rows = rows.filter((row) => {
          return [
            row.service_code,
            row.service_description,
            row.domain,
            row.provider_display,
          ]
            .join(" ")
            .toLowerCase()
            .includes(query);
        });
      }

      return rows;
    },
    totalAuthorized() {
      return this.rows.reduce((sum, row) => sum + Number(row.authorized_amount || 0), 0);
    },
    totalThisMonth() {
      return this.rows.reduce((sum, row) => sum + Number(row.expenses_this_month || 0), 0);
    },
    totalExpenses() {
      return this.rows.reduce((sum, row) => sum + Number(row.total_expenses || 0), 0);
    },
    totalRemaining() {
      return this.rows.reduce((sum, row) => sum + Number(row.amount_remaining || 0), 0);
    },
    filteredAuthorized() {
      return this.filteredRows.reduce((sum, row) => sum + Number(row.authorized_amount || 0), 0);
    },
    filteredThisMonth() {
      return this.filteredRows.reduce((sum, row) => sum + Number(row.expenses_this_month || 0), 0);
    },
    filteredExpenses() {
      return this.filteredRows.reduce((sum, row) => sum + Number(row.total_expenses || 0), 0);
    },
    filteredRemaining() {
      return this.filteredRows.reduce((sum, row) => sum + Number(row.amount_remaining || 0), 0);
    },
  },
  methods: {
    formatCurrency(value) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(Number(value || 0));
    },
    formatDate(value) {
      if (!value) return "—";

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return value;
      }

      return new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(date);
    },
    usagePercent(row) {
      const authorized = Number(row.authorized_amount || 0);
      const spent = Number(row.total_expenses || 0);

      if (authorized <= 0) {
        return spent > 0 ? 100 : 0;
      }

      return (spent / authorized) * 100;
    },
    openDetail(row) {
      this.selectedRow = row;
    },
    closeDetail() {
      this.selectedRow = null;
    },
    async fetchExpenditure() {
      this.isLoading = true;
      this.errorMessage = "";

      try {
        const token = localStorage.getItem("token");

        const { data } = await axios.get(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/fetch_client_spending_history`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        this.plans = data.client_budget_history || [];
      } catch (error) {
        console.error("Error fetching expenditure:", error);
        this.errorMessage = "Unable to load expenditure records. Please try again.";
      } finally {
        this.isLoading = false;
      }
    },
  },
  mounted() {
    this.fetchExpenditure();
  },
};
</script>

<style scoped>
button {
  border: 1px solid #e2e8f0;
}

button:hover {
  transform: translateY(-1px);
}

.expenditure-table th,
.expenditure-table td {
  border-bottom: 1px solid #e5e7eb;
}

.min-w-40 {
  min-width: 10rem;
}

.min-w-48 {
  min-width: 12rem;
}

.min-w-56 {
  min-width: 14rem;
}

.min-w-64 {
  min-width: 16rem;
}

.min-w-72 {
  min-width: 18rem;
}

.usage-bar {
  width: 100%;
  height: 8px;
  border-radius: 999px;
  background: #e5e7eb;
  overflow: hidden;
}

.usage-bar-fill {
  height: 100%;
  border-radius: 999px;
}

.usage-normal {
  background: #38bdf8;
}

.usage-over {
  background: #ef4444;
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 100;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.modal-card {
  width: 100%;
  max-width: 1100px;
  max-height: 85vh;
  overflow-y: auto;
  background: #ffffff;
  border-radius: 14px;
  padding: 1.25rem;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.25);
}
</style>
